/**
 * API クライアント。すべての画面はここを通す。
 *
 * API ベース: 本番ビルドは VITE_KUMO_API_URL（ダッシュボードを別オリジンに置く場合）、
 * 無ければ同一オリジン（開発時は Vite が /v1 をプロキシ）。ドメインはコードに書かない。
 *
 * 認証: 開発者セッショントークン（kds_…）を localStorage に置き、Authorization ヘッダで送る。
 * Cookie を使わないのは API 全体の方針（CORS credentials:false・CSRF の面を作らない）に合わせるため。
 */

import { withAcceptLanguage } from './acceptLanguage';
import { getLocale } from './i18n';
import { PRODUCT_NAME } from './productName';
import type { ProjectRole } from './projectMembers';

export const API_BASE = (import.meta.env.VITE_KUMO_API_URL as string | undefined)?.replace(/\/+$/, '') ?? '';

/** ゲームに貼る `<script src>` 用の API の絶対 URL（同一オリジン運用なら今のオリジン） */
export const publicApiUrl = () => API_BASE || window.location.origin;

const TOKEN_KEY = 'kumo.console.token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* プライベートモード等。セッションはメモリ上だけになる */
  }
}

export interface Issue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
  }
  /** 未実装のエンドポイント（他モジュールが並行開発中）。Fastify の既定 404 は error.code を持たない */
  get notAvailable() {
    return this.code === 'route_missing' || (this.status === 404 && this.code === 'not_implemented');
  }
  get issues(): Issue[] {
    const list = (this.details as { issues?: Issue[] } | undefined)?.issues;
    return Array.isArray(list) ? list : [];
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
/** セッション切れ（401）を App に知らせてログイン画面へ戻す */
export function onUnauthorized(fn: Listener) {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
}

// raw = 生のバイト（hosting の blob の PUT・アップロードのページ uploadFiles.ts・2026-10-04 creator/web-upload）。JSON にせずそのまま送る
export async function api<T>(path: string, init: { method?: string; body?: unknown; raw?: Uint8Array; auth?: boolean; headers?: Record<string, string> } = {}): Promise<T> {
  // headers: Idempotency-Key など（お金を動かす POST の再送で二重に動かさない。money/routes.ts）
  // Accept-Language = いまの画面の言語（ブラウザの既定より優先）。サーバーは確認メール等の言語をこれで選ぶ（acceptLanguage.ts・english-pass E1）
  const headers: Record<string, string> = withAcceptLanguage({ ...(init.headers ?? {}) }, getLocale());
  const token = getToken();
  if (init.auth !== false && token) headers.authorization = `Bearer ${token}`;
  if (init.raw) headers['content-type'] = 'application/octet-stream';
  else if (init.body !== undefined) headers['content-type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: init.method ?? 'GET',
      headers,
      body: init.raw ? (init.raw as BodyInit) : init.body !== undefined ? JSON.stringify(init.body) : undefined
    });
  } catch {
    throw new ApiError(0, 'network_error', `Cannot reach the ${PRODUCT_NAME} API. Is the server running?`);
  }
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)?.error;
    // ルートが無い = このサーバーにはまだ無い機能。Hono の notFound（apps/server/src/app.ts）は
    // { code: 'not_found', message: 'Route GET /v1/... not found' } を返すので、それを route_missing に読み替える。
    // WHY: 以前は Fastify の形（error が文字列）だけを見ていて、Hono に移った後は「まだ使えない」の落ち着いた表示が
    // どの画面でも出ず、赤い「見つかりません。削除された可能性があります」になっていた（dashboard-review 2026-09-27）
    if (res.status === 404 && err?.code === 'not_found' && typeof err.message === 'string' && /^Route [A-Z]+ \//.test(err.message)) {
      throw new ApiError(404, 'route_missing', 'This feature is not available on this server yet');
    }
    if (err && typeof err === 'object' && err.code) {
      // 401 = セッション切れ → ログイン画面へ。ただし invalid_password（パスキーの追加で打ったパスワードの誤り・money/passkeys.ts）は
      // セッションは生きているので戻さない（打ち間違い 1 回でログアウトされ、入力中の画面が消えるのを防ぐ）。
      // reauthentication_failed（アカウントの閉鎖で打ったパスワードの誤り・platform/accountClose.ts）も同じ理由で戻さない
      const reauthTypo = err.code === 'invalid_password' || err.code === 'reauthentication_failed';
      if (res.status === 401 && init.auth !== false && !reauthTypo) unauthorizedListeners.forEach((l) => l());
      throw new ApiError(res.status, err.code, err.message ?? 'Request failed', err.details);
    }
    // Fastify の「ルートが無い」404 は { message, error: 'Not Found', statusCode } の形 = 機能がまだ無い
    if (res.status === 404) throw new ApiError(404, 'route_missing', 'This feature is not available on this server yet');
    throw new ApiError(res.status, 'http_error', (json as { message?: string } | null)?.message ?? `HTTP ${res.status}`);
  }
  return json as T;
}

// ---------------------------------------------------------------- types

export type EnvName = 'development' | 'production';
export const ENVS: EnvName[] = ['development', 'production'];

export interface Developer {
  id: string;
  email: string;
  displayName: string | null;
  /** メール確認済みか（0013 identity）。古いサーバーは返さない = undefined は「分からない」なので帯を出さない */
  emailVerified?: boolean;
  /**
   * 閉鎖を頼んだ時刻（null = 普通のアカウント・古いサーバーは undefined）。閉鎖後もログインと出金はできるが、
   * 新しいゲームは作れない（platform/accountClose.ts）。pages/AccountPage.tsx が閉鎖の画面の代わりに説明を出す
   */
  closedAt?: string | null;
  /**
   * パスワードを持っているか（2026-09-27 core/dev-oauth）。false = Google / Apple / GitHub だけで登録した人。
   * 再認証（閉鎖・ログイン方法の追加 / 解除）でパスワード欄の代わりに「Google で確認」を出す（components/Reauth.tsx）。
   * 古いサーバーは返さない = undefined は「持っている」とみなす（今までどおりパスワード欄を出す）
   */
  hasPassword?: boolean;
}

export interface ProjectSummary {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  /** 持ち主か共同作業者か（2026-10-22 core/handoff-1b）。無い = 古いサーバー = 持ち主（projectMembers.ts の isOwnerRole） */
  role?: ProjectRole;
}

export interface KeyInfo {
  id: string;
  kind: 'publishable' | 'secret';
  prefix: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface IssuedKey {
  id: string;
  kind: 'publishable' | 'secret';
  environment: EnvName;
  label: string | null;
  token: string;
  createdAt: string;
}

export interface ProjectDetail {
  id: string;
  name: string;
  slug: string;
  /** 持ち主か共同作業者か（2026-10-22 core/handoff-1b）。画面の「持ち主だけ」の出し分けに使う（components/OwnerOnly.tsx） */
  role?: ProjectRole;
  environments: Array<{ id: string; name: EnvName; keys: KeyInfo[] }>;
}

export interface CreatedProject {
  id: string;
  name: string;
  slug: string;
  environments: Array<{ id: string; name: EnvName; keys: IssuedKey[] }>;
  /** メールが未確認で sk_ が作られなかった（作成の応答の secretKeysWithheld・2026-09-28 Core SEC-DEVLOGIN-01）。画面が案内を出す */
  secretKeysWithheld?: boolean;
}

export interface Overview {
  players: { total: number; new24h: number; new7d: number; active24h: number; banned: number };
  dailyActive: Array<{ date: string; players: number }>;
  config: { version: number; pushedAt: string | null; pushedBy: string | null };
  keys: { active: number; revoked: number };
}

export interface PlayerRow {
  id: string;
  displayName: string | null;
  email: string | null;
  providers: string[];
  isGuest: boolean;
  createdAt: string;
  lastSeenAt: string;
  banned: boolean;
  bannedAt: string | null;
  banReason: string | null;
}

/**
 * BAN の異議申し立て 1 件（GET …/appeals。apps/server/src/modules/integrity/sanctions.ts の listAppeals）。
 * reason = BAN のときに書いた理由（運営の内部メモ・プレイヤーには見えない）、message = プレイヤーの申し立て
 */
export interface AppealRow {
  id: string;
  sanctionId: string;
  playerId: string;
  kind: string;
  reason: string;
  message: string;
  status: 'open' | 'accepted' | 'rejected';
  createdAt: string;
}

export interface PlayerDetail {
  id: string;
  displayName: string | null;
  createdAt: string;
  lastSeenAt: string;
  banned: boolean;
  bannedAt: string | null;
  banReason: string | null;
  isGuest: boolean;
  identities: Array<{ provider: string; email?: string; linkedAt: string }>;
  activeSessions: number;
  stats: Record<string, number>;
  achievements: Array<{ key: string; period: string; unlockedAt: string }>;
  inventory: { currencies: Record<string, number>; items: Record<string, number> };
}

export interface AuditEntry {
  id: number;
  actor: string;
  /** 自分の操作の時だけ入る（他の開発者のメールはサーバーが返さない・2026-10-21） */
  actorEmail: string | null;
  /** actor が開発者の時の、自分から見た相手。開発者でない actor（キー・system）は null */
  actorRole?: 'you' | 'owner' | 'collaborator' | 'previous_owner' | 'other_developer' | null;
  action: string;
  environment: EnvName | null;
  detail: Record<string, unknown>;
  createdAt: string;
}

export type LocalText = string | Record<string, string>;
/** config の多言語テキスト（{ en, ja, … }）を表示中の言語で選ぶ。無ければ en → 最初の値 */
export const textOf = (t: LocalText | undefined | null) =>
  t == null ? '' : typeof t === 'string' ? t : t[getLocale()] ?? t.en ?? Object.values(t)[0] ?? '';

export interface GameConfig {
  stats?: Array<{ key: string; title?: LocalText; aggregation?: string }>;
  leaderboards?: Array<{ key: string; title?: LocalText; stat: string; order?: string; reset?: string; format?: string }>;
  achievements?: Array<{ key: string; title: LocalText; hidden?: boolean }>;
  currencies?: Array<{ key: string }>;
  items?: Array<{ key: string }>;
  products?: Array<{ key: string; title: LocalText; price: number; currency?: string }>;
  multiplayer?: { modes?: Array<{ key: string; title?: LocalText; minPlayers?: number; maxPlayers?: number }> };
  [k: string]: unknown;
}

export interface LeaderboardView {
  leaderboard: { key: string; title: LocalText | null; stat: string; order: string; reset: string; format: string };
  period: string;
  currentPeriod: string;
  previousPeriod: string | null;
  total: number;
  entries: Array<{ rank: number; playerId: string; displayName: string | null; value: number }>;
  /** 次のページ（hooks.ts の usePagedResource が読む）。null = もう無い。2026-10-04 に足した = 古いサーバーには無い */
  nextCursor?: string | null;
}

export interface AchievementsView {
  players: number;
  achievements: Array<{
    key: string; title: LocalText; description: LocalText | null; hidden: boolean; reset: string;
    condition: { stat: string; gte?: number; lte?: number } | null; unlockedBy: number;
  }>;
}

export interface RealtimeView {
  live: { rooms: number; players: number; queued: number; modes: Record<string, { rooms: number; players: number; queued: number }> };
  recent: Array<{
    id: string; mode: string; private: boolean; origin: string; createdAt: string; endedAt: string;
    durationSeconds: number; peakPlayers: number; totalPlayers: number; endReason: string;
  }>;
  last24h: { rooms: number; players: number; avgDurationSeconds: number };
}

export interface Deployment {
  id: string;
  version: number;
  status: string;
  active: boolean;
  message: string | null;
  totalBytes: number;
  fileCount: number;
  createdBy: string;
  createdAt: string;
  finalizedAt: string | null;
  failureReason: string | null;
}

export interface HostingView {
  environment: EnvName;
  slug: string;
  url: string | null;
  hostUrl: string | null;
  pathUrl: string | null;
  active: (Deployment & { activatedAt: string; activatedBy: string }) | null;
  deployments: Deployment[];
}

/** 'fake' = 組み込みの偽決済（鍵の無いローカル）/ 'test' = Stripe テストモード / 'live' = 本物 */
export type PaymentMode = 'fake' | 'test' | 'live';

export type PurchaseStatus = 'pending' | 'completed' | 'canceled' | 'expired' | 'failed' | 'refunded' | 'disputed';

export interface ConnectAccount {
  mode: PaymentMode;
  provider: string;
  accountId: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  updatedAt: string;
}

/** GET …/environments/:env/payments（金額はすべて最小通貨単位: usd ならセント、jpy なら円） */
export interface PaymentsView {
  environment: EnvName;
  /** null = このサーバーでは課金が使えない（production で Stripe の鍵が無い） */
  mode: PaymentMode | null;
  feeBps: number;
  totals: Array<{
    currency: string; purchases: number; gross: number; refunds: number; disputes: number; net: number;
    feeEstimate: number; developerEstimate: number;
  }>;
  /** 30 日・UTC・古い順。gross は通貨ごと */
  daily: Array<{ date: string; purchases: number; gross: Record<string, number> }>;
  recent: PurchaseRow[];
  connect: ConnectAccount | null;
}

export interface PurchaseRow {
  id: string;
  /** null = プレイヤーは削除済み（購入は会計記録として匿名化して残る。server の modules/privacy） */
  playerId: string | null;
  playerName: string | null;
  productKey: string;
  title: string;
  amount: number;
  currency: string;
  status: PurchaseStatus;
  mode: PaymentMode;
  refundedAmount: number;
  createdAt: string;
  completedAt: string | null;
}

export interface GrantLine {
  currency?: string;
  item?: string;
  count: number;
}

/**
 * 表示用の鍵の頭。サーバーの display_prefix は旧実装だと本体の大半を含みうる（base64url の '_' で切っていた）ため、
 * 画面では「種別タグ + 4 文字」までに必ず切り詰める（画面共有・スクショで鍵を漏らさない）。
 */
export function keyHead(prefix: string) {
  const m = /^((?:pk|sk)_(?:dev|live)_)(.{0,4})/.exec(prefix);
  return m ? `${m[1]}${m[2]}…` : `${prefix.replace(/…$/, '').slice(0, 12)}…`;
}

export const envPath = (projectId: string, env: EnvName) => `/v1/projects/${projectId}/environments/${env}`;

/**
 * 認証付きでファイルを受け取って保存させる（明細の CSV）。<a href> ではヘッダ（Authorization）を付けられないので fetch → Blob。
 * WHY Cookie にしないか: API 全体が Bearer 方式（冒頭のコメント）。ダウンロードのためだけに Cookie の面を作らない
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = getToken();
  let res: Response;
  try {
    // 明細の CSV の見出しなども画面の言語で受け取れるよう、api() と同じく画面の言語を付ける
    res = await fetch(`${API_BASE}${path}`, { headers: withAcceptLanguage(token ? { authorization: `Bearer ${token}` } : {}, getLocale()) });
  } catch {
    throw new ApiError(0, 'network_error', `Cannot reach the ${PRODUCT_NAME} API. Is the server running?`);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(res.status, body?.error?.code ?? 'http_error', body?.error?.message ?? `HTTP ${res.status}`);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** お金を動かす POST の Idempotency-Key（画面を開いてからの 1 回の操作に 1 つ。ボタンの連打・通信断の再送で二重に動かさない） */
export const newIdempotencyKey = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// ---------------------------------------------------------------- sign-in methods（0013 identity）

export type OAuthProviderName = 'google' | 'discord' | 'apple' | 'x';
export const OAUTH_PROVIDERS: OAuthProviderName[] = ['google', 'discord', 'apple', 'x'];

export interface OAuthProviderState {
  provider: OAuthProviderName;
  enabled: boolean;
  configured: boolean;
  clientId: string | null;
  /** Apple だけ */
  teamId?: string | null;
  keyId?: string | null;
  /** 保存済みの秘密の末尾（例: "…cdef"）。秘密そのものは API が返さない */
  secretHint: string | null;
  /**
   * own = 自分で登録したアプリ / kumo = 共用 X アプリで動いている（X だけ・sharedXApp のときだけ）/ null = 未設定。
   * 古いサーバーは返さないので任意（無ければ configured = own とみなす）
   */
  credentials?: 'own' | 'kumo' | null;
  updatedAt: string | null;
  /** 各プロバイダの開発者コンソールに登録してもらう URL */
  callbackUrl: string;
}

export interface AuthSettings {
  redirectUrls: string[];
  /** サーバーに KUMO_SECRETS_KEY が無い（production）= 秘密を保存できない */
  secretsConfigured: boolean;
  /**
   * このサーバーで X の共用アプリが使えるか（X の承認済みの旗 + 資格情報。players/oauth/xManaged.ts sharedXAppAvailable）。
   * false / 無い（既定）なら X 欄は「自分の X アプリを入れる」だけ
   */
  sharedXApp?: boolean;
  providers: OAuthProviderState[];
  /**
   * KUMODeck でログイン（2026-10-07 core/kumodeck-signin・サーバー players/account/）。鍵が無いので providers の表とは別。
   * available = サーバー全体の旗（KUMO_ACCOUNT_SIGNIN）。false / 無い（古いサーバー）なら行を出さない
   */
  accountSignIn?: { available: boolean; enabled: boolean };
}

/**
 * 機能の ON/OFF（2026-09-27 task/toggles・GET …/features）。すべて既定 OFF。切り替えは kumo.config.json の push。
 * ダッシュボードからも確認つきで切り替えられる（PUT …/config。components/FeaturesCard.tsx・featureToggle.ts）。
 * サーバーの modules/config/features.ts が一覧の正
 */
export interface FeatureState {
  key: string;
  group: string;
  title: string;
  /** どこに書けば ON になるか（`features.saves` / `ads.enabled` / `xkumo functions enable`） */
  configPath: string;
  source: 'features' | 'section' | 'module' | 'api';
  enabled: boolean | null;
  /** ON にするには先にこれらも ON（サーバーは依存が欠けた push を拒否する）。画面の切り替えで一緒に変える */
  requires?: string[];
}
export interface FeaturesView {
  environment: EnvName;
  version: number;
  features: FeatureState[];
}
